import { capitalize } from 'lodash'
import { BridgeId, FlowType, FromCarbonPayloadType, getBridgeNameFromBridgeId, ToCarbonPayloadType } from 'src/constants/BlockchainConstants'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(duration)

// Customize the relative time output to short form
dayjs.locale('en', {
  ...dayjs.Ls.en,
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few sec',
    m: '1 min',
    mm: '%d mins',
    h: '1 hr',
    hh: '%d hrs',
    d: '1 day',
    dd: '%d days',
    M: '1 mth',
    MM: '%d mths',
    y: '1 yr',
    yy: '%d yrs',
  },
})

export const maskHash = (hash: string, len: number) => {
  if (hash.length > len + 3) {
    return hash.slice(0, len) + '...' + hash.slice(-3)
  } else {
    return hash
  }
}

export const uuidv4 = () => {
  // eslint-disable-line import/prefer-default-export
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0 // eslint-disable-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8 // eslint-disable-line no-bitwise
    return v.toString(16)
  })
}

export const formatBridgeId = (bridgeId: BridgeId) => {
  if (!Object.values(BridgeId).includes(bridgeId)) {
    return '---'
  }
  return getBridgeNameFromBridgeId(bridgeId)
}


export const formatChain = (chain: string) => {
  switch (chain) {
    case 'eth':
      return 'Ethereum'
    case 'zil':
      return 'Zilliqa'
    case 'bsc':
      return 'BNB Chain'
    case 'neo':
      return 'NEO'
    case 'neo3':
      return 'NEO3'
    case 'polynetwork':
      return 'PolyNetwork'
    case undefined:
    case null:
      return '---'
    default:
      return capitalize(chain)
  }
}

export const formatRelayStatus = (status: string) => {
  if (status === 'in_transit') {
    return 'In Transit'
  } else if (status === 'completed') {
    return 'Complete'
  } else if (status === 'refunded') {
    return 'Refunded'
  } else {
    return 'Error'
  }
}

export const formatPayloadType = (bridgeId: BridgeId, flowType: FlowType, payloadType: string) => {
  if (!bridgeId || !flowType) {
    return ''
  }
  if (bridgeId !== BridgeId.Axelar) {
    return capitalize(payloadType)
  }
  if (flowType === FlowType.In) {
    return toCarbonPayloadTypeToString(payloadType as ToCarbonPayloadType)
  } else if (flowType === FlowType.Out) {
    return fromCarbonPayloadTypeToString(payloadType as FromCarbonPayloadType)
  }
}

function toCarbonPayloadTypeToString(value: ToCarbonPayloadType): string {
  switch (value) {
    case ToCarbonPayloadType.Deposit:
      return 'Deposit'
    case ToCarbonPayloadType.DepositAndExecute:
      return 'DepositAndExecute'
    case ToCarbonPayloadType.ExecuteCarbon:
      return 'ExecuteCarbon'
    case ToCarbonPayloadType.ConfirmationCallback:
      return 'ConfirmationCallback'
    default:
      return 'Unknown'
  }
}

function fromCarbonPayloadTypeToString(value: FromCarbonPayloadType): string {
  switch (value) {
    case FromCarbonPayloadType.Withdraw:
      return 'Withdraw'
    case FromCarbonPayloadType.ExecuteGateway:
      return 'ExecuteGateway'
    case FromCarbonPayloadType.WithdrawAndExecute:
      return 'WithdrawAndExecute'
    case FromCarbonPayloadType.RegisterToken:
      return 'RegisterToken'
    case FromCarbonPayloadType.DeregisterToken:
      return 'DeregisterToken'
    default:
      return 'Unknown'
  }
}

export function formatTime(input: string | number | Date): string {
  if (!input) return '-'
  const date = dayjs(input)
  return `${date.format('YYYY-MM-DD HH:mm:ss')}`
}

export function formatUtc(input: string | number | Date): string {
  const date = dayjs(input)
  if (!date.isValid()) return '-'
  return date.utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC'
}

export function formatAge(input: string | number | Date): string {
  const date = dayjs(input)
  return `${date.fromNow()}`
}

export function formatAgeShort(input: string | number | Date): string {
  const now = dayjs()
  const past = dayjs(input)
  if (!past.isValid()) return '-'

  const diff = now.diff(past)
  const dur = dayjs.duration(diff)

  if (dur.asSeconds() < 60) {
    return `${Math.floor(dur.asSeconds())}s`
  } else if (dur.asMinutes() < 60) {
    return `${Math.floor(dur.asMinutes())}m`
  } else if (dur.asHours() < 24) {
    return `${Math.floor(dur.asHours())}h`
  } else if (dur.asDays() < 30) {
    return `${Math.floor(dur.asDays())}d`
  } else if (dur.asMonths() < 12) {
    return `${Math.floor(dur.asMonths())}M`
  } else {
    return `${Math.floor(dur.asYears())}y`
  }
}

function utcEpochToDate(epoch: number): string {
  const date = new Date((epoch * 1000) / 1000)
  const utcTime = date.toUTCString()
  return utcTime
}

export const formatUTCEpochTime = (time: number) => {
  if (!time) return '-'
  const utcEpoch = time // UTC epoch timestamp
  const date = utcEpochToDate(utcEpoch)
  return formatTime(date)
}

export function formatDurationShort(start: number | string | Date, end: number | string | Date): string | null {
  if (!start || !end) return null

  const toMs = (value: any) => {
    if (typeof value === 'number' && value < 1e12) {
      return value * 1000 // likely Unix seconds
    }
    return Number(value) // already ms or date string
  }

  const diffMs = Math.abs(toMs(end) - toMs(start))
  const dur = dayjs.duration(diffMs)

  if (dur.asSeconds() < 60) {
    return `${Math.round(dur.asSeconds())}s`
  } else if (dur.asMinutes() < 60) {
    return `${Math.round(dur.asMinutes())}m`
  } else {
    return `${Math.round(dur.asHours())}h`
  }
}

export function getUtcTimestampNHoursAgo(hours: number): string {
  return dayjs().subtract(hours, 'hour').utc().format('YYYY-MM-DD HH:mm:ss')
}

export const formatSnakeCaseToTitleCase = (input: string) => {
  const words = input.split('_')
  const capitalize = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  )
  const output = capitalize.join(' ')
  return output
}

// copied from https://github.com/axios/axios/blob/fe7d09bb08fa1c0e414956b7fc760c80459b0a43/lib/helpers/combineURLs.js
export function combineURLs(baseURL: string, relativeURL: string) {
  return relativeURL
    ? baseURL.replace(/\/+$/, '') + '/' + relativeURL.replace(/^\/+/, '')
    : baseURL
}

// strips 0x from a string
export function strip0x(str: string) {
  return stripPrefix(str, ['0x'])
}

// strips anything in the list of prefixes from a string
export function stripPrefix(str: string, prefixes: string[]): string {
  const pattern = new RegExp(`^(${prefixes.join('|')})`, 'i')
  return str.replace(pattern, '')
}

export function formatUsd(rawUsd: string) {
  const n = Number(rawUsd)

  // fallback for undefined / NaN
  if (!isFinite(n)) return '-'

  // treat anything smaller than 1 cent but non-zero as “<0.01”
  if (Math.abs(n) > 0 && Math.abs(n) < 0.01) return '< $0.01'

  // otherwise show up to 2 decimals, trimming trailing zeros
  const formatted = n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
  return `$${formatted}`
}

export function stripTrailingZeros(n: string | number): string {
  const s = String(n)

  if (!s.includes('.')) return s          // nothing to strip

  return s
    .replace(/(\.\d*?[1-9])0+$/, '$1')    // 1.2300 → 1.23
    .replace(/\.0+$/, '')                 // 1.0 → 1
    .replace(/\.$/, '')                   // 1. → 1
}
